import { useEffect, useRef } from 'react';

/**
 * One LIFO stack per claimed key (Escape, ⌘S), shared by every settings layer.
 *
 * Capture-phase listeners fire in registration order, so a listener per layer
 * gave the press to the OLDEST layer. One document listener and an explicit
 * stack give it to the newest layer that is not buried under a dialog.
 *
 * Module state, deliberately: the input is a document-wide keydown, and the
 * stack mirrors that scope.
 */
interface KeyLayer {
  /** Returns false to decline the press, letting it fall through. */
  readonly fire: () => boolean | void;
  /**
   * The element this layer speaks for. A layer whose owner is outside the
   * topmost dialog is buried — newer than the dialog, but under its scrim.
   */
  readonly owner?: () => Element | null;
}

/**
 * Open dialogs, oldest first. Registration order, not DOM order: a dialog
 * rendered inline at its JSX position can precede one opened before it.
 */
const dialogStack: Element[] = [];

export function pushDialogLayer(el: () => Element | null): () => void {
  const node = el();
  if (node === null) return () => {};
  dialogStack.push(node);
  return () => {
    const at = dialogStack.indexOf(node);
    if (at !== -1) dialogStack.splice(at, 1);
  };
}

function topDialog(): Element | undefined {
  for (let i = dialogStack.length - 1; i >= 0; i -= 1) {
    const el = dialogStack[i]!;
    if (el.isConnected) return el;
  }
  return undefined;
}

/**
 * With a dialog open, containment decides: a layer the topmost dialog does not
 * contain is buried, and one it does contain is live wherever focus sits in
 * it (an inline edit must outlive a Tab to the next control). With none open,
 * focus decides: a layer stands down while focus is somewhere specific outside
 * its owner. Focus on <body> is nowhere in particular and counts for every
 * layer. An owner-less layer is buried whenever any dialog is open.
 */
function covered(layer: KeyLayer): boolean {
  const el = layer.owner?.();
  const top = topDialog();
  if (el === undefined || el === null) return top !== undefined;
  if (!el.isConnected) return true;
  if (top !== undefined) return !top.contains(el);
  const active = document.activeElement;
  return active !== null && active !== document.body && !el.contains(active);
}

const keyLayers = new Map<string, KeyLayer[]>();
const keyListeners = new Map<string, (e: globalThis.KeyboardEvent) => void>();

function matches(key: string, e: globalThis.KeyboardEvent): boolean {
  // Mid-composition the press belongs to the IME.
  if (e.isComposing) return false;
  if (key === 'Escape') return e.key === 'Escape';
  return (
    e.key.toLowerCase() === 's' &&
    (e.metaKey || e.ctrlKey) &&
    !e.altKey &&
    !e.shiftKey
  );
}

/** Escape has no browser default; ⌘S has the browser's Save dialog. */
function claim(key: string, e: globalThis.KeyboardEvent): void {
  if (key !== 'Escape') e.preventDefault();
  e.stopImmediatePropagation();
}

/** Uncovered layers, newest first — the order a press is offered to them. */
function liveLayers(key: string): KeyLayer[] {
  const current = keyLayers.get(key) ?? [];
  const live: KeyLayer[] = [];
  for (let i = current.length - 1; i >= 0; i -= 1) {
    const candidate = current[i]!;
    if (!covered(candidate)) live.push(candidate);
  }
  return live;
}

/** Offer the press down the stack until a layer takes it. */
function offer(key: string): boolean {
  for (const layer of liveLayers(key)) {
    if (layer.fire() !== false) return true;
  }
  return false;
}

/**
 * Fire the topmost layer for `key` without a key press, for gestures that mean
 * the same thing (a press on the settings backdrop is a dismissal, like
 * Escape). Reports whether a layer consumed it.
 */
export function fireTopKeyLayer(key: string): boolean {
  return offer(key);
}

export function pushKeyLayer(key: string, layer: KeyLayer): () => void {
  const stack = keyLayers.get(key) ?? [];
  if (stack.length === 0) {
    const listener = (e: globalThis.KeyboardEvent): void => {
      if (!matches(key, e)) return;
      // Offer it down the stack; a throwing layer still consumed the press
      // (otherwise ⌘S falls through to the browser's Save dialog).
      let consumed: boolean;
      try {
        consumed = offer(key);
      } catch (err) {
        claim(key, e);
        // eslint-disable-next-line no-console
        console.error('settings key layer threw', err);
        return;
      }
      if (consumed) {
        claim(key, e);
        return;
      }
      // Nothing took it, but while any Save layer exists ⌘S stays the app's:
      // a buried or refused save must not open the browser's Save dialog.
      if (key !== 'Escape' && stack.length > 0) e.preventDefault();
    };
    keyLayers.set(key, stack);
    keyListeners.set(key, listener);
    document.addEventListener('keydown', listener, true);
  }
  stack.push(layer);
  keyLayers.set(key, stack);
  return () => {
    const at = stack.indexOf(layer);
    if (at !== -1) stack.splice(at, 1);
    if (stack.length === 0) {
      const listener = keyListeners.get(key);
      if (listener !== undefined) {
        document.removeEventListener('keydown', listener, true);
        keyListeners.delete(key);
      }
    }
  };
}

/** Claim Escape for this layer while `active`. */
export function useEscapeLayer(
  active: boolean,
  onEscape: () => boolean | void,
  owner?: () => Element | null,
): void {
  useLayer('Escape', active, onEscape, owner);
}

/** Claim ⌘S / Ctrl-S for this layer while `active`. */
export function useSaveShortcut(
  active: boolean,
  onSave: () => boolean | void,
  owner?: () => Element | null,
): void {
  useLayer('Save', active, onSave, owner);
}

function useLayer(
  key: string,
  active: boolean,
  fire: () => boolean | void,
  owner?: () => Element | null,
): void {
  const fireRef = useRef(fire);
  const ownerRef = useRef(owner);
  useEffect(() => {
    fireRef.current = fire;
    ownerRef.current = owner;
  });
  useEffect(() => {
    if (!active) return undefined;
    return pushKeyLayer(key, {
      fire: () => fireRef.current(),
      owner: () => ownerRef.current?.() ?? null,
    });
  }, [key, active]);
}
